import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Check, ImageOff, Loader2 } from 'lucide-react'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from 'components/ui/dialog'
import { Button } from 'components/ui/button'
import { cn } from 'lib/utils'
import { initializeCaptureImages } from 'slices/captureImageSlice'
import { initializeMyLocationImages } from 'slices/locationImageSlice'
import { mergeLibraryImages, type LibraryImage } from 'utils/libraryImages'

export interface ImageLibraryPickerProps {
	open: boolean
	onClose: () => void
	/** The chosen images, in the order they were picked. */
	onConfirm: (images: LibraryImage[]) => void
	/** Overrides the dialog copy, which differs slightly between composers. */
	title?: string
	description?: string
}

const formatDay = (iso: string): string => {
	const when = new Date(iso)
	return Number.isNaN(when.getTime())
		? ''
		: when.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

/**
 * Choose images you already have, to put into the note being written.
 *
 * Shared by the field composer and the desktop one. It lived under
 * `features/Field` until MYD-469 and nothing about it was field-specific
 * except its address and a grid sized for a 390px screen. Writing a second one
 * for the desktop is how two pickers drift into disagreeing about what the
 * library contains.
 *
 * **Both collections**, via `mergeLibraryImages`: the field roll and images
 * already filed against a record. A photograph is a photograph, and a rep
 * writing a note at a desk has no reason to care which table it came from.
 *
 * **Captures already in a note are hidden by default.** Forty photographs from
 * one visit are worked through rather than browsed, so the ones still waiting
 * to be filed are the ones being looked for. The toggle brings everything back,
 * because a shot can legitimately belong in a second note.
 *
 * That filter deliberately says *captures*. `usedAt` is a capture-roll idea
 * meaning "pulled into a note"; a filed image has no equivalent, so calling it
 * "unused" would label every desktop upload as something still waiting to be
 * filed. Filed images are therefore always shown.
 *
 * Three across on a phone, more as the screen allows. A picker needs a tap
 * target and a visible tick, and at four across on a 390px screen the tick
 * covers the picture it is confirming.
 */
export function ImageLibraryPicker({
	open,
	onClose,
	onConfirm,
	title = 'Choose from your images',
	description = 'Tap to select, then add them to this note.',
}: ImageLibraryPickerProps) {
	const dispatch = useDispatch()
	/*
		Selected field by field rather than destructured off the slice.

		Two reasons. A screen that never loads one of these slices should not
		crash the picker, and `state.locationImages` being absent is a real
		state, not a bug to assert against. And returning a fresh `?? {}` object
		from a selector gives a new reference on every store change, which
		re-renders this component constantly.
	*/
	const entities = useSelector((state: any) => state.captureImages?.entities)
	const ids = useSelector((state: any) => state.captureImages?.ids)
	const isLoading = useSelector((state: any) => state.captureImages?.isLoading)
	const filedEntities = useSelector((state: any) => state.locationImages?.entities)
	const filedIds = useSelector((state: any) => state.locationImages?.ids)

	const [chosen, setChosen] = useState<string[]>([])
	const [showUsed, setShowUsed] = useState(false)

	useEffect(() => {
		if (!open) return
		setChosen([])
		setShowUsed(false)
		// Refetched on open: a roll shot minutes ago on this phone will not be in
		// a store loaded when the screen first mounted.
		;(dispatch as any)(initializeCaptureImages(undefined))
		;(dispatch as any)(initializeMyLocationImages())
	}, [open, dispatch])

	const images = useMemo<LibraryImage[]>(
		() =>
			mergeLibraryImages(
				(ids || []).map((id: string) => entities?.[id]).filter(Boolean),
				(filedIds || []).map((id: string) => filedEntities?.[id]).filter(Boolean),
			),
		[entities, ids, filedEntities, filedIds],
	)

	/*
		Only a CAPTURE can be hidden here.

		`usedAt` means "already pulled into a note", which is a fact about a roll
		being worked through. A filed image has no such state, so filtering on
		`!usedAt` alone would quietly let every desktop upload masquerade as
		something still waiting to be filed.
	*/
	const usedCount = images.filter(image => image.usedAt).length

	const shown = useMemo(
		() => (showUsed ? images : images.filter(image => !image.usedAt)),
		[images, showUsed],
	)

	const byId = useMemo(() => new Map(images.map(image => [image.id, image])), [images])

	const toggle = useCallback((id: string) => {
		setChosen(previous =>
			previous.includes(id) ? previous.filter(candidate => candidate !== id) : [...previous, id],
		)
	}, [])

	const confirm = useCallback(() => {
		// In the order they were picked, which is the order they will appear in
		// the note.
		onConfirm(chosen.map(id => byId.get(id)).filter(Boolean) as LibraryImage[])
		onClose()
	}, [chosen, byId, onConfirm, onClose])

	if (!open) return null

	return (
		<Dialog open onClose={onClose}>
			<DialogContent className="max-w-sm sm:max-w-2xl">
				<DialogHeader>
					<DialogTitle>{title}</DialogTitle>
					<DialogDescription>{description}</DialogDescription>
				</DialogHeader>

				<div className="p-5 pt-2">
					{usedCount > 0 && (
						<button
							type="button"
							onClick={() => setShowUsed(previous => !previous)}
							className="mb-2 text-xs font-medium text-primary"
						>
							{showUsed ? 'Hide captures already in a note' : `Show all ${images.length}`}
						</button>
					)}

					{isLoading && images.length === 0 ? (
						<p className="flex items-center gap-2 py-8 text-sm text-gray-500">
							<Loader2 className="h-4 w-4 animate-spin" />
							Loading your images…
						</p>
					) : shown.length === 0 ? (
						<div className="py-8 text-center">
							<ImageOff className="mx-auto mb-2 h-6 w-6 text-gray-300" />
							<p className="text-sm font-medium text-gray-800">
								{images.length > 0
									? 'Everything here is already in a note'
									: 'No images yet'}
							</p>
							<p className="mt-1 text-sm text-gray-500">
								{images.length > 0
									? 'Show all to reuse one.'
									: 'Capture a set in the field, or add one to a record first.'}
							</p>
						</div>
					) : (
						<ul className="grid max-h-[46svh] grid-cols-3 gap-1.5 overflow-y-auto sm:max-h-[50vh] sm:grid-cols-5">
							{shown.map(image => {
								const picked = chosen.includes(image.id)
								const order = chosen.indexOf(image.id) + 1
								return (
									<li key={image.id}>
										<button
											type="button"
											onClick={() => toggle(image.id)}
											aria-pressed={picked}
											aria-label={`${image.context || image.name || "Image"}, ${formatDay(image.takenAt)}`}
											className="relative block w-full"
										>
											<img
												src={image.url}
												alt=""
												loading="lazy"
												className={cn(
													'aspect-square w-full rounded-md border object-cover',
													picked ? 'border-primary' : 'border-gray-200',
												)}
											/>
											{picked && (
												// The number, not just a tick: with several chosen it
												// says what order they will appear in the note.
												<span className="absolute right-1 top-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-white">
													{order}
												</span>
											)}
										</button>
									</li>
								)
							})}
						</ul>
					)}

					<div className="flex gap-2 pt-4">
						<Button variant="ghost" isInput onClick={onClose} className="flex-1">
							Cancel
						</Button>
						<Button
							variant="primary"
							isInput
							onClick={confirm}
							disabled={chosen.length === 0}
							className="flex-1"
						>
							<Check className="mr-2 h-4 w-4" />
							{chosen.length > 0 ? `Add ${chosen.length}` : 'Add'}
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	)
}

export default ImageLibraryPicker
